import dynamic from 'helpers/dynamic';
import type { GetByPowerstepTwoSuccess } from 'autogen/swagger/all';
import { GoToCheckout } from 'autogen/translation-keys/trans-website-basket';
import { Button, ButtonWithSpinner } from 'components/Button';
import WithIcon from 'components/hoc/withIcon/WithIcon';
import useOnLegacyModalOpenEffect from 'hooks/globals/useOnLegacyModalOpenEffect';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import useIsAuthenticated from 'hooks/user/useIsAuthenticated';
import type { MatasPlusRecommendationsSuccess } from 'autogen/swagger/all';
import type { QueryObserverResult } from '@tanstack/react-query';
import { useGetByPowerstepTwoQuery } from 'autogen/swagger/all/hooks';
import { useMatasPlusRecommendationsQuery } from 'autogen/swagger/all/hooks';
import useBasketPowerstepQuickView from './shared/hooks/useBasketPowerstepQuickView';
import type { LinkAction as LinkActionType } from 'components/shared/Routing/components/LinkAction/shared/types';
import LinkActionButton from 'components/Button/LinkActionButton';
import Icon from 'components/Icon';

const StyledButton = styled(LinkActionButton)`
    width: 100%;
`;

const StyledButtonWithSpinner = styled(ButtonWithSpinner)`
    width: 100%;
`;

export default function BasketPowerstepOpener({ checkoutLinkAction }: { checkoutLinkAction: LinkActionType }) {
    const [{ isOpen: isBasketQuickViewOpen }, toggle] = useBasketPowerstepQuickView();

    const { refetch: getByPowerstepTwo } = useGetByPowerstepTwoQuery({}, { enabled: false });
    const { refetch: matasPlusRecommendations } = useMatasPlusRecommendationsQuery({
        enabled: false
    });

    const { t } = useTranslation();
    const [hasBeenOpened, setHasBeenOpened] = useState(false);
    const isAuthenticated = useIsAuthenticated();

    // if a legacy modal opened close the quickview. only necessary while powerstep is using legacy modal.
    useOnLegacyModalOpenEffect((legacyModal) => {
        if (legacyModal && isBasketQuickViewOpen) {
            toggle((prevModel) => ({ ...prevModel, isOpen: false }));
        }
    });

    const OpenBasketPowerstepOrRedirect = async () => {
        try {
            const data:
                | undefined
                | [
                      QueryObserverResult<GetByPowerstepTwoSuccess>,
                      QueryObserverResult<MatasPlusRecommendationsSuccess> | undefined
                  ] = await Promise.race([
                Promise.all([getByPowerstepTwo(), isAuthenticated ? matasPlusRecommendations() : undefined]),
                new Promise<undefined>((resolve, reject) => {
                    setTimeout(() => {
                        reject(new Error('Fetching powerstep took too long'));
                    }, 3000);
                })
            ]);

            const [powerstepTwoData, matasPlusRecommendationData] = data || [];
            const hasNoData =
                !powerstepTwoData?.data?.recommendationModels.length &&
                !matasPlusRecommendationData?.data?.products.length;

            if (hasNoData) {
                throw new Error('No powerstep data received');
            }

            toggle({
                isOpen: true,
                matasPlusRecommendationModel: matasPlusRecommendationData?.data,
                powerstepTwoModel: powerstepTwoData?.data,
                checkoutUrl: checkoutLinkAction.url
            });
            setHasBeenOpened(true);
        } catch (error) {
            window.location.href = checkoutLinkAction.url;
            console.error(error);
        }
    };

    if (hasBeenOpened) {
        return (
            <StyledButton size="lg" action={checkoutLinkAction}>
                <WithIcon icon={<Icon type="ChevronRight" size={24} />} position="right">
                    {t(GoToCheckout)}
                </WithIcon>
            </StyledButton>
        );
    }

    return (
        <StyledButtonWithSpinner
            css={css`
                width: 100%;
            `}
            size="lg"
            onClick={OpenBasketPowerstepOrRedirect}
        >
            <WithIcon icon={<Icon type="ChevronRight" size={24} />} position="right">
                {t(GoToCheckout)}
            </WithIcon>
        </StyledButtonWithSpinner>
    );
}
